import React, { useState, useEffect } from 'react';
import { CubeIcon, ArrowDownTrayIcon, EyeIcon } from '@heroicons/react/24/outline';
import inventoryApi from '../../../services/inventoryApi';
import LoadingSpinner from '../../Common/LoadingSpinner';
import CommonDataTable from '../../Common/CommonDataTable';
import CommonModal from '../../Common/CommonModal';

const StockLevelsReport = () => {
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [stockData, setStockData] = useState([]);
  const [summary, setSummary] = useState({});
  const [warehouses, setWarehouses] = useState([]);
  const [categories, setCategories] = useState([]);

  const [filters, setFilters] = useState({
    warehouseId: '',
    categoryId: '',
    lowStock: false,
    outOfStock: false
  });

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItemMovements, setSelectedItemMovements] = useState([]);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadStockLevels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const loadInitialData = async () => {
    try {
      const [warehousesRes, categoriesRes] = await Promise.all([
        inventoryApi.getWarehouses(),
        inventoryApi.getCategories({ limit: 1000 })
      ]);
      setWarehouses(warehousesRes.data?.data || warehousesRes.data || []);
      setCategories(categoriesRes.data?.data || categoriesRes.data || []);
    } catch (error) {
      console.error('Error loading initial data:', error);
    }
  };

  const loadStockLevels = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.warehouseId) params.warehouseId = filters.warehouseId;
      if (filters.categoryId) params.categoryId = filters.categoryId;
      if (filters.lowStock) params.lowStock = true;
      if (filters.outOfStock) params.outOfStock = true;

      const response = await inventoryApi.getStockLevelsReport(params);
      const data = response?.data || response;

      setStockData(data.stockLevels || []);
      setSummary(data.summary || {});
    } catch (error) {
      console.error('Error loading stock levels:', error);
      setStockData([]);
      setSummary({});
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({ warehouseId: '', categoryId: '', lowStock: false, outOfStock: false });
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {};
      if (filters.warehouseId) params.warehouseId = filters.warehouseId;
      if (filters.categoryId) params.categoryId = filters.categoryId;
      if (filters.lowStock) params.lowStock = true;
      if (filters.outOfStock) params.outOfStock = true;

      const response = await inventoryApi.getStockLevelsReportCSV(params);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `stock-levels-report-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting data:', error);
    } finally {
      setExporting(false);
    }
  };

  const movementColumnsForModal = [
    {
      key: 'transactionDate',
      header: 'Date',
      render: (value, row) => (
        <div className="text-sm">
          {new Date(row.transactionDate).toLocaleDateString()}
          <div className="text-xs text-gray-500">{new Date(row.transactionDate).toLocaleTimeString()}</div>
        </div>
      )
    },
    { key: 'warehouse', header: 'Warehouse', render: (value, row) => row.warehouse?.name },
    {
      key: 'movementType',
      header: 'Type',
      render: (value, row) => (
        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-800">
          {row.movementType?.replace('_', ' ').toUpperCase()}
        </span>
      )
    },
    {
      key: 'quantity',
      header: 'Quantity',
      render: (value, row) => (
        <div className={`text-right font-semibold ${row.direction === 'in' ? 'text-green-600' : 'text-red-600'}`}>
          {row.direction === 'in' ? '+' : '-'}{row.quantity} {row.unit}
        </div>
      )
    },
    {
      key: 'totalCost',
      header: 'Total Cost',
      render: (value, row) => <div className="text-right">Rs {row.totalCost?.toFixed(2) || '0.00'}</div>
    }
  ];

  const viewItemDetails = async (item) => {
    setSelectedItem(item);
    setLoading(true);
    try {
      const params = { itemId: item.itemId, warehouseId: filters.warehouseId || '', limit: 50 };
      const response = await inventoryApi.getMovementsReport(params);
      const data = response?.data || response;
      setSelectedItemMovements(data.movements || []);
    } catch (error) {
      console.error('Error loading item movements:', error);
      setSelectedItemMovements([]);
    } finally {
      setLoading(false);
      setShowDetailsModal(true);
    }
  };

  const columns = [
    {
      key: 'itemName',
      header: 'Item Name',
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="font-semibold text-gray-900">{row.itemName}</div>
          <div className="text-sm text-gray-500">SKU: {row.sku}</div>
        </div>
      )
    },
    { key: 'category', header: 'Category', sortable: true },
    { key: 'warehouseName', header: 'Warehouse', sortable: true },
    {
      key: 'currentQuantity',
      header: 'Current Stock',
      sortable: true,
      render: (value, row) => (
        <div className="text-right">
          <div className={`font-semibold ${
            row.status === 'OUT_OF_STOCK' ? 'text-red-600' :
            row.status === 'LOW_STOCK' ? 'text-amber-600' :
            'text-green-600'
          }`}>
            {row.currentQuantity} {row.unit}
          </div>
        </div>
      )
    },
    {
      key: 'availableQuantity',
      header: 'Available',
      sortable: true,
      render: (value, row) => <div className="text-right">{row.availableQuantity} {row.unit}</div>
    },
    {
      key: 'reservedQuantity',
      header: 'Reserved',
      sortable: true,
      render: (value, row) => <div className="text-right text-amber-600">{row.reservedQuantity} {row.unit}</div>
    },
    {
      key: 'reorderPoint',
      header: 'Reorder Point',
      sortable: true,
      render: (value, row) => <div className="text-right text-gray-600">{row.reorderPoint} {row.unit}</div>
    },
    {
      key: 'totalValue',
      header: 'Value',
      sortable: true,
      render: (value, row) => <div className="text-right font-semibold">Rs {row.totalValue?.toFixed(2) || '0.00'}</div>
    },
    {
      key: 'status',
      header: 'Status',
      render: (value, row) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          row.status === 'OUT_OF_STOCK' ? 'bg-red-100 text-red-800' :
          row.status === 'LOW_STOCK' ? 'bg-amber-100 text-amber-800' :
          'bg-green-100 text-green-800'
        }`}>
          {row.status?.replace('_', ' ')}
        </span>
      )
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (value, row) => (
        <button onClick={() => viewItemDetails(row)} className="text-indigo-600 hover:text-indigo-900 font-medium text-sm">
          <EyeIcon className="h-4 w-4" />
        </button>
      )
    }
  ];

  return (
    <div className="flex flex-col h-[calc(100vh-64px)]">
      {/* Filters Card */}
      <div className="flex-shrink-0 px-4 sm:px-6 lg:px-8 pt-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="bg-gradient-to-r from-indigo-600 to-indigo-500 px-5 py-3 flex items-center gap-3 rounded-t-xl">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <CubeIcon className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-white">Stock Level Report</h2>
              <p className="text-indigo-200 text-xs">Current stock quantities and value across warehouses</p>
            </div>
          </div>

          <div className="px-5 py-4 flex flex-wrap items-end gap-4">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Warehouse</label>
              <select
                value={filters.warehouseId}
                onChange={(e) => handleFilterChange('warehouseId', e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm bg-gray-50 hover:bg-white transition-colors min-w-[180px]"
              >
                <option value="">All Warehouses</option>
                {warehouses.map(w => <option key={w._id} value={w._id}>{w.name}</option>)}
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Category</label>
              <select
                value={filters.categoryId}
                onChange={(e) => handleFilterChange('categoryId', e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm bg-gray-50 hover:bg-white transition-colors min-w-[180px]"
              >
                <option value="">All Categories</option>
                {categories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
            </div>

            <div className="flex items-center gap-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={filters.lowStock}
                  onChange={(e) => handleFilterChange('lowStock', e.target.checked)}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="ml-2 text-sm text-gray-700">Low Stock Only</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={filters.outOfStock}
                  onChange={(e) => handleFilterChange('outOfStock', e.target.checked)}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="ml-2 text-sm text-gray-700">Out of Stock Only</span>
              </label>
            </div>

            <div className="flex-1" />

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={clearFilters}
                className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-800 transition-colors flex items-center gap-1.5"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Reset
              </button>
              <button
                type="button"
                onClick={handleExport}
                disabled={exporting}
                className="px-5 py-2 text-sm font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:bg-green-300 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-2 shadow-sm"
              >
                <ArrowDownTrayIcon className="w-4 h-4" />
                {exporting ? 'Exporting...' : 'Export CSV'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex-grow flex items-center justify-center">
          <LoadingSpinner size="lg" fullScreen={false} />
        </div>
      )}

      {/* Content */}
      {!loading && (
        <div className="flex-grow overflow-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="space-y-4">
            {summary && Object.keys(summary).length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-2">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-sm font-medium text-gray-600 mb-1">Total Items</h3>
                  <p className="text-2xl font-bold text-indigo-600">{summary.totalItems || 0}</p>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-sm font-medium text-gray-600 mb-1">Total Value</h3>
                  <p className="text-2xl font-bold text-green-600">Rs {summary.totalValue?.toFixed(2) || '0.00'}</p>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-sm font-medium text-gray-600 mb-1">Low Stock</h3>
                  <p className="text-2xl font-bold text-amber-600">{summary.lowStockItems || 0}</p>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-sm font-medium text-gray-600 mb-1">Out of Stock</h3>
                  <p className="text-2xl font-bold text-red-600">{summary.outOfStockItems || 0}</p>
                </div>
              </div>
            )}

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-indigo-600 px-4 py-3 flex items-center justify-between">
                <span className="text-sm font-medium text-white">Stock Levels ({stockData.length})</span>
              </div>
              <CommonDataTable
                data={stockData}
                columns={columns}
                searchable={true}
                searchPlaceholder="Search items..."
                emptyMessage="No stock data available"
              />
            </div>
          </div>
        </div>
      )}

      <CommonModal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        title={`Stock Details - ${selectedItem?.itemName}`}
        size="xl"
      >
        {selectedItem && (
          <div className="space-y-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Current Stock</p>
                  <p className="text-lg font-semibold text-gray-900">{selectedItem.currentQuantity} {selectedItem.unit}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Available</p>
                  <p className="text-lg font-semibold text-green-600">{selectedItem.availableQuantity} {selectedItem.unit}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Reserved</p>
                  <p className="text-lg font-semibold text-amber-600">{selectedItem.reservedQuantity} {selectedItem.unit}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Value</p>
                  <p className="text-lg font-semibold text-gray-900">Rs {selectedItem.totalValue?.toFixed(2) || '0.00'}</p>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Recent Movements</h4>
              {selectedItemMovements.length > 0 ? (
                <CommonDataTable
                  data={selectedItemMovements}
                  columns={movementColumnsForModal}
                  emptyMessage="No movements found for this item"
                />
              ) : (
                <p className="text-gray-500 text-center py-4">No movements found for this item</p>
              )}
            </div>
          </div>
        )}
      </CommonModal>
    </div>
  );
};

export default StockLevelsReport;
